// src/components/charts/EnergyAdditionBarChart.js
import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { formatNumber, scaleNumber } from "../utils/utils";

/**
 * Provides a bar chart for visualizing energy additions..
 * @param {Array} labels - Labels for the x-axis.
 * @param {Array} values - Values for the bars.
 * @param {string} chartColor - Color of the bars.
 * @param {string} yAxisLabel - Label for the y-axis.
 * @param {boolean} isAnnual - If true, the chart displays annual data; otherwise, it displays monthly data.
 * @param {Array} countAddition - Array of counts for additions.
 * @param {Array} countReduction - Array of counts for reductions.
 * @param {Array} powerAddition - Array of power additions.
 * @param {Array} powerReduction - Array of power reductions.
 * @param {string} unit - Unit of measurement for the values (e.g., "MW", "MWp").
 * @returns {JSX.Element} - Returns a bar chart component.
 */

const EnergyAdditionBarChart = ({
  labels,
  values,
  chartColor,
  yAxisLabel,
  isAnnual = true,
  countAddition,
  countReduction,
  powerAddition,
  powerReduction,
  unit,
}) => {

  // useMemo for performance optimization
  const data = useMemo(() => (
    labels.map((label, idx) => ({
      label,
      nettoZubau: values[idx],
      countAdd: countAddition?.[idx],
      countRed: countReduction?.[idx],
      powerAdd: powerAddition?.[idx],
      powerRed: powerReduction?.[idx],
    }))
  ), [labels, values, countAddition, countReduction, powerAddition, powerReduction]);

  if (!labels || !values || labels.length === 0 || values.length === 0) {
    return <p>Keine Daten verfügbar</p>;
  }

  const displayUnit = unit ?? "";

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length > 0) {
      const d = payload[0].payload;
      return (
        <div className="bg-white border border-gray-300 p-2 rounded text-sm text-black text-left">
          <p className="font-semibold">{label}</p>
          <p>Netto Zubau: {formatNumber(scaleNumber(d.nettoZubau, 1000), 1, 1)} {displayUnit}</p>
          <p>Installierte Leistung: {formatNumber(scaleNumber(d.powerAdd, 1000), 1, 1)} {displayUnit}</p>
          <p>Installierte Anlagen: {formatNumber(d.countAdd, 0, 1)}</p>
          <p>Stillgelegte Leistung: {formatNumber(scaleNumber(d.powerRed, 1000), 1, 1)} {displayUnit}</p>
          <p>Stillgelegte Anlagen: {formatNumber(d.countRed, 0, 1)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={data}
        margin={{ top: 20, right: 20, bottom: 40, left: 20 }}
        style={{ color: "black" }} // set text color to black
      >
        <ReferenceLine 
          y={0} 
          stroke="#000" 
          strokeDasharray="4 2" 
        />
        <XAxis
          dataKey="label"
          label={{
            value: isAnnual ? "Jahr" : "Monat/Jahr",
            position: "insideBottom",
            offset: -10,
            fill: "black", // set x-axis label color to black
            style: { color: "black" }
          }}
          tick={{ fill: "black" }}
        />
        <YAxis
          label={{
            value: yAxisLabel,
            angle: -90,
            position: "insideLeft",
            fill: "black", // set y-axis label color to black
            style: { color: "black" }
          }}
          tick={{ fill: "black" }}
          tickFormatter={(value) => formatNumber(scaleNumber(value, 1000), 0, 0)}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="nettoZubau" fill={chartColor} barSize={40} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default EnergyAdditionBarChart;
